// Theme contract: 5-level color token architecture
//
// FOUNDATION (internal) → STRUCTURAL → SEMANTIC → DOMAIN → DATA
//
// All themes must implement this identical shape.
// Domain tokens derive from semantic — never raw hex.
// No raw hex in visualization code — only token references.
//
// CSS theme tokens are generated from this file.
// Run: node scripts/generate-theme-css.mjs
// Token mapping: shared/constants/theme-token-map.js

// Tax treatment → categorical slot mapping (change here to recolor all themes)
const TX_SLOT = { taxable: 4, taxFree: 1, taxDeferred: 2, conditional: 3 }

// ─── DARK THEME ──────────────────────────────────────────────

const darkSemantic = {
  success: '#27ae60',
  danger:  '#e94560',
  warning: '#f39c12',
  info:    '#3498db',
  neutral: '#95a5a6',
  accent:  '#818cf8',
}

const darkCategorical = ['#4db8a0', '#9878c0', '#b87898', '#b88860', '#7898b0', '#8ca058']

const dark = {
  // Level 2 — Structural
  surface: {
    base:     '#1a1a2e',
    elevated: '#16213e',
    overlay:  '#374151',
  },

  border: {
    subtle:  '#2a3952',
    default: '#2a3f5f',
    strong:  '#3b4f73',
  },

  grid: {
    major: '#2a3f5f',
    minor: '#2a3952',
  },

  text: {
    primary:   '#ffffff',
    secondary: '#d1d5db',
    muted:     '#666666',
    bright:    '#ffffff',
    link:      '#d1d5db',
  },

  interactive: {
    focus:     '#3498db',
    hover:     '#2a3f5f',
    active:    '#16213e',
    selection: '#00ff9f',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.85,
  },

  // Level 3 — Semantic
  semantic: darkSemantic,

  // Level 4 — Domain (derives from semantic, resolved at definition time)
  domain: {
    dividendLifecycle: {
      declare: darkSemantic.danger,
      ex:      darkSemantic.warning,
      record:  darkSemantic.info,
      pay:     darkSemantic.success,
    },

    payments: {
      accent: darkSemantic.accent,
      status: {
        paidRecorded:      darkSemantic.success,
        paidUnrecorded:    darkSemantic.danger,
        expectedAnnounced: darkSemantic.info,
        expectedProjected: darkSemantic.warning,
      },
    },

    taxTreatment: {
      taxable:     darkCategorical[TX_SLOT.taxable],
      taxDeferred: darkCategorical[TX_SLOT.taxDeferred],
      taxFree:     darkCategorical[TX_SLOT.taxFree],
      conditional: darkCategorical[TX_SLOT.conditional],
    },

    calendar: {
      today:      darkSemantic.info,
      holiday:    darkSemantic.danger,
      earlyClose: darkSemantic.warning,
    },
  },

  // Level 5 — Data
  data: {
    categorical: darkCategorical,
    sequential: {
      low:  '#0a3d5c',
      mid:  '#1a8a8a',
      high: '#00ff9f',
    },
    diverging: {
      negative: '#ff8888',
      neutral:  '#888888',
      positive: '#88ff88',
    },
  },
}

// ─── LIGHT THEME ─────────────────────────────────────────────

const lightSemantic = {
  success: '#16a34a',
  danger:  '#dc2626',
  warning: '#d97706',
  info:    '#2563eb',
  neutral: '#6b7280',
  accent:  '#4f46e5',
}

const lightCategorical = ['#0e7a70', '#6830b0', '#904870', '#7a5028', '#486078', '#4a6818']

const light = {
  // Level 2 — Structural
  surface: {
    base:     '#ffffff',
    elevated: '#f9fafb',
    overlay:  '#eceef2',
  },

  border: {
    subtle:  '#f0f0f0',
    default: '#e0e0e0',
    strong:  '#d1d5db',
  },

  grid: {
    major: '#e0e0e0',
    minor: '#e9eaec',
  },

  text: {
    primary:   '#111827',
    secondary: '#374151',
    muted:     '#9ca3af',
    bright:    '#1a2030',
    link:      '#4f46e5',
  },

  interactive: {
    focus:     '#2563eb',
    hover:     '#f3f4f6',
    active:    '#e5e7eb',
    selection: '#0d9488',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.12,
    moderate: 0.3,
    strong:   0.65,
  },

  // Level 3 — Semantic
  semantic: lightSemantic,

  // Level 4 — Domain (derives from semantic, resolved at definition time)
  domain: {
    dividendLifecycle: {
      declare: lightSemantic.danger,
      ex:      lightSemantic.warning,
      record:  lightSemantic.info,
      pay:     lightSemantic.success,
    },

    payments: {
      accent: lightSemantic.accent,
      status: {
        paidRecorded:      lightSemantic.success,
        paidUnrecorded:    lightSemantic.danger,
        expectedAnnounced: lightSemantic.info,
        expectedProjected: lightSemantic.warning,
      },
    },

    taxTreatment: {
      taxable:     lightCategorical[TX_SLOT.taxable],
      taxDeferred: lightCategorical[TX_SLOT.taxDeferred],
      taxFree:     lightCategorical[TX_SLOT.taxFree],
      conditional: lightCategorical[TX_SLOT.conditional],
    },

    calendar: {
      today:      lightSemantic.info,
      holiday:    lightSemantic.danger,
      earlyClose: lightSemantic.warning,
    },
  },

  // Level 5 — Data
  data: {
    categorical: lightCategorical,
    sequential: {
      low:  '#dbeafe',
      mid:  '#60a5fa',
      high: '#2563eb',
    },
    diverging: {
      negative: '#b91c1c',
      neutral:  '#6b7280',
      positive: '#15803d',
    },
  },
}

// ─── SOLARIZED DARK ──────────────────────────────────────────

const solarizedDarkSemantic = {
  success: '#859900',
  danger:  '#dc322f',
  warning: '#b58900',
  info:    '#268bd2',
  neutral: '#93a1a1',
  accent:  '#2aa198',
}

const solarizedDarkCategorical = ['#5aa0a0', '#8870a8', '#a87888', '#a08058', '#7888a0', '#808850']

const solarizedDark = {
  surface: {
    base:     '#002b36',
    elevated: '#073642',
    overlay:  '#001f27',
  },

  border: {
    subtle:  '#093c44',
    default: '#0a3a46',
    strong:  '#14505c',
  },

  grid: {
    major: '#0e5060',
    minor: '#0b4450',
  },

  text: {
    primary:   '#fdf6e3',
    secondary: '#93a1a1',
    muted:     '#657b83',
    bright:    '#fdf6e3',
    link:      '#93a1a1',
  },

  interactive: {
    focus:     '#268bd2',
    hover:     '#0a3a46',
    active:    '#073642',
    selection: '#2aa198',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.85,
  },

  semantic: solarizedDarkSemantic,

  domain: {
    dividendLifecycle: {
      declare: solarizedDarkSemantic.danger,
      ex:      solarizedDarkSemantic.warning,
      record:  solarizedDarkSemantic.info,
      pay:     solarizedDarkSemantic.success,
    },
    payments: {
      accent: solarizedDarkSemantic.accent,
      status: {
        paidRecorded:      solarizedDarkSemantic.success,
        paidUnrecorded:    solarizedDarkSemantic.danger,
        expectedAnnounced: solarizedDarkSemantic.info,
        expectedProjected: solarizedDarkSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     solarizedDarkCategorical[TX_SLOT.taxable],
      taxDeferred: solarizedDarkCategorical[TX_SLOT.taxDeferred],
      taxFree:     solarizedDarkCategorical[TX_SLOT.taxFree],
      conditional: solarizedDarkCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      solarizedDarkSemantic.info,
      holiday:    solarizedDarkSemantic.danger,
      earlyClose: solarizedDarkSemantic.warning,
    },
  },

  data: {
    categorical: solarizedDarkCategorical,
    sequential: {
      low:  '#073642',
      mid:  '#268bd2',
      high: '#2aa198',
    },
    diverging: {
      negative: '#dc322f',
      neutral:  '#93a1a1',
      positive: '#859900',
    },
  },
}

// ─── SOLARIZED LIGHT ────────────────────────────────────────

const solarizedLightSemantic = {
  success: '#859900',
  danger:  '#dc322f',
  warning: '#b58900',
  info:    '#268bd2',
  neutral: '#657b83',
  accent:  '#2aa198',
}

const solarizedLightCategorical = ['#2a7878', '#6848a0', '#885870', '#6a5030', '#486070', '#4a5828']

const solarizedLight = {
  surface: {
    base:     '#fdf6e3',
    elevated: '#eee8d5',
    overlay:  '#e7dfc6',
  },

  border: {
    subtle:  '#e3ddca',
    default: '#d8d2bd',
    strong:  '#c5bea7',
  },

  grid: {
    major: '#d8d2bd',
    minor: '#ddd5bc',
  },

  text: {
    primary:   '#073642',
    secondary: '#586e75',
    muted:     '#657b83',
    bright:    '#0a3038',
    link:      '#586e75',
  },

  interactive: {
    focus:     '#268bd2',
    hover:     '#eee8d5',
    active:    '#e7dfc6',
    selection: '#2aa198',
  },

  opacity: {
    faint:    0.04,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.85,
  },

  semantic: solarizedLightSemantic,

  domain: {
    dividendLifecycle: {
      declare: solarizedLightSemantic.danger,
      ex:      solarizedLightSemantic.warning,
      record:  solarizedLightSemantic.info,
      pay:     solarizedLightSemantic.success,
    },
    payments: {
      accent: solarizedLightSemantic.accent,
      status: {
        paidRecorded:      solarizedLightSemantic.success,
        paidUnrecorded:    solarizedLightSemantic.danger,
        expectedAnnounced: solarizedLightSemantic.info,
        expectedProjected: solarizedLightSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     solarizedLightCategorical[TX_SLOT.taxable],
      taxDeferred: solarizedLightCategorical[TX_SLOT.taxDeferred],
      taxFree:     solarizedLightCategorical[TX_SLOT.taxFree],
      conditional: solarizedLightCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      solarizedLightSemantic.info,
      holiday:    solarizedLightSemantic.danger,
      earlyClose: solarizedLightSemantic.warning,
    },
  },

  data: {
    categorical: solarizedLightCategorical,
    sequential: {
      low:  '#eee8d5',
      mid:  '#268bd2',
      high: '#2aa198',
    },
    diverging: {
      negative: '#dc322f',
      neutral:  '#93a1a1',
      positive: '#859900',
    },
  },
}

// ─── MIDNIGHT ───────────────────────────────────────────────

const midnightSemantic = {
  success: '#00c896',
  danger:  '#ff5c7c',
  warning: '#ffb020',
  info:    '#4da3ff',
  neutral: '#8a94a6',
  accent:  '#00e0ff',
}

const midnightCategorical = ['#50c0b0', '#a080c8', '#c080a0', '#c09068', '#80a0b8', '#90a860']

const midnight = {
  surface: {
    base:     '#0b1220',
    elevated: '#121a2b',
    overlay:  '#1a2438',
  },

  border: {
    subtle:  '#1a2438',
    default: '#22304a',
    strong:  '#2e3e5e',
  },

  grid: {
    major: '#22304a',
    minor: '#1a2438',
  },

  text: {
    primary:   '#ffffff',
    secondary: '#d6deeb',
    muted:     '#6b7a90',
    bright:    '#ffffff',
    link:      '#d6deeb',
  },

  interactive: {
    focus:     '#4da3ff',
    hover:     '#1a2438',
    active:    '#121a2b',
    selection: '#00e0ff',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.9,
  },

  semantic: midnightSemantic,

  domain: {
    dividendLifecycle: {
      declare: midnightSemantic.danger,
      ex:      midnightSemantic.warning,
      record:  midnightSemantic.info,
      pay:     midnightSemantic.success,
    },
    payments: {
      accent: midnightSemantic.accent,
      status: {
        paidRecorded:      midnightSemantic.success,
        paidUnrecorded:    midnightSemantic.danger,
        expectedAnnounced: midnightSemantic.info,
        expectedProjected: midnightSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     midnightCategorical[TX_SLOT.taxable],
      taxDeferred: midnightCategorical[TX_SLOT.taxDeferred],
      taxFree:     midnightCategorical[TX_SLOT.taxFree],
      conditional: midnightCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      midnightSemantic.info,
      holiday:    midnightSemantic.danger,
      earlyClose: midnightSemantic.warning,
    },
  },

  data: {
    categorical: midnightCategorical,
    sequential: {
      low:  '#121a2b',
      mid:  '#4da3ff',
      high: '#00e0ff',
    },
    diverging: {
      negative: '#ff5c7c',
      neutral:  '#8a94a6',
      positive: '#00c896',
    },
  },
}

// ─── PAPER ──────────────────────────────────────────────────

const paperSemantic = {
  success: '#3a9640',
  danger:  '#cc3830',
  warning: '#d08a10',
  info:    '#2070b8',
  neutral: '#6b7280',
  accent:  '#7c3aed',
}

const paperCategorical = ['#186858', '#5828a0', '#804060', '#704820', '#405068', '#406010']

const paper = {
  surface: {
    base:     '#fafaf7',
    elevated: '#f1f1ec',
    overlay:  '#e5e3db',
  },

  border: {
    subtle:  '#e5e3db',
    default: '#d6d3c9',
    strong:  '#bdb9aa',
  },

  grid: {
    major: '#d6d3c9',
    minor: '#dddbd5',
  },

  text: {
    primary:   '#1f2937',
    secondary: '#4b5563',
    muted:     '#6b7280',
    bright:    '#2c1e10',
    link:      '#4b5563',
  },

  interactive: {
    focus:     '#1565c0',
    hover:     '#e5e3db',
    active:    '#d6d3c9',
    selection: '#7c3aed',
  },

  opacity: {
    faint:    0.04,
    subtle:   0.12,
    moderate: 0.5,
    strong:   0.85,
  },

  semantic: paperSemantic,

  domain: {
    dividendLifecycle: {
      declare: paperSemantic.danger,
      ex:      paperSemantic.warning,
      record:  paperSemantic.info,
      pay:     paperSemantic.success,
    },
    payments: {
      accent: paperSemantic.accent,
      status: {
        paidRecorded:      paperSemantic.success,
        paidUnrecorded:    paperSemantic.danger,
        expectedAnnounced: paperSemantic.info,
        expectedProjected: paperSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     paperCategorical[TX_SLOT.taxable],
      taxDeferred: paperCategorical[TX_SLOT.taxDeferred],
      taxFree:     paperCategorical[TX_SLOT.taxFree],
      conditional: paperCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      paperSemantic.info,
      holiday:    paperSemantic.danger,
      earlyClose: paperSemantic.warning,
    },
  },

  data: {
    categorical: paperCategorical,
    sequential: {
      low:  '#f1f1ec',
      mid:  '#1565c0',
      high: '#7c3aed',
    },
    diverging: {
      negative: '#c62828',
      neutral:  '#6b7280',
      positive: '#2e7d32',
    },
  },
}

// ─── HIGH CONTRAST ──────────────────────────────────────────

const highContrastSemantic = {
  success: '#00a000',
  danger:  '#ff0033',
  warning: '#cc9900',
  info:    '#0088cc',
  neutral: '#bfbfbf',
  accent:  '#ff00ff',
}

const highContrastCategorical = ['#00d0c0', '#b080ff', '#ff80b0', '#d0a050', '#80b8d0', '#b0d050']

const highContrast = {
  surface: {
    base:     '#000000',
    elevated: '#111111',
    overlay:  '#222222',
  },

  border: {
    subtle:  '#333333',
    default: '#666666',
    strong:  '#999999',
  },

  grid: {
    major: '#666666',
    minor: '#333333',
  },

  text: {
    primary:   '#ffffff',
    secondary: '#e0e0e0',
    muted:     '#bfbfbf',
    bright:    '#ffffff',
    link:      '#e0e0e0',
  },

  interactive: {
    focus:     '#00b3ff',
    hover:     '#222222',
    active:    '#111111',
    selection: '#ff00ff',
  },

  opacity: {
    faint:    0.06,
    subtle:   0.2,
    moderate: 0.6,
    strong:   1,
  },

  semantic: highContrastSemantic,

  domain: {
    dividendLifecycle: {
      declare: highContrastSemantic.danger,
      ex:      highContrastSemantic.warning,
      record:  highContrastSemantic.info,
      pay:     highContrastSemantic.success,
    },
    payments: {
      accent: highContrastSemantic.accent,
      status: {
        paidRecorded:      highContrastSemantic.success,
        paidUnrecorded:    highContrastSemantic.danger,
        expectedAnnounced: highContrastSemantic.info,
        expectedProjected: highContrastSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     highContrastCategorical[TX_SLOT.taxable],
      taxDeferred: highContrastCategorical[TX_SLOT.taxDeferred],
      taxFree:     highContrastCategorical[TX_SLOT.taxFree],
      conditional: highContrastCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      highContrastSemantic.info,
      holiday:    highContrastSemantic.danger,
      earlyClose: highContrastSemantic.warning,
    },
  },

  data: {
    categorical: highContrastCategorical,
    sequential: {
      low:  '#111111',
      mid:  '#00b3ff',
      high: '#ffffff',
    },
    diverging: {
      negative: '#ff0033',
      neutral:  '#bfbfbf',
      positive: '#00ff00',
    },
  },
}

// ─── TROPIC DARK ───────────────────────────────────────────

const tropicDarkSemantic = {
  success: '#80D4B1',
  danger:  '#DF715F',
  warning: '#CBA83F',
  info:    '#0F4C81',
  neutral: '#7a8a8e',
  accent:  '#73F6FF',
}

const tropicDarkCategorical = ['#50b8a8', '#9880b0', '#b88098', '#b08860', '#7898a8', '#8ca058']

const tropicDark = {
  surface: {
    base:     '#0a1e2e',
    elevated: '#0f2a3d',
    overlay:  '#163044',
  },

  border: {
    subtle:  '#163044',
    default: '#1d3f58',
    strong:  '#2a5270',
  },

  grid: {
    major: '#1d3f58',
    minor: '#163044',
  },

  text: {
    primary:   '#ffffff',
    secondary: '#d4e0e8',
    muted:     '#5e7a8a',
    bright:    '#ffffff',
    link:      '#d4e0e8',
  },

  interactive: {
    focus:     '#0F4C81',
    hover:     '#163044',
    active:    '#0f2a3d',
    selection: '#73F6FF',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.85,
  },

  semantic: tropicDarkSemantic,

  domain: {
    dividendLifecycle: {
      declare: tropicDarkSemantic.danger,
      ex:      tropicDarkSemantic.warning,
      record:  tropicDarkSemantic.info,
      pay:     tropicDarkSemantic.success,
    },
    payments: {
      accent: tropicDarkSemantic.accent,
      status: {
        paidRecorded:      tropicDarkSemantic.success,
        paidUnrecorded:    tropicDarkSemantic.danger,
        expectedAnnounced: tropicDarkSemantic.info,
        expectedProjected: tropicDarkSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     tropicDarkCategorical[TX_SLOT.taxable],
      taxDeferred: tropicDarkCategorical[TX_SLOT.taxDeferred],
      taxFree:     tropicDarkCategorical[TX_SLOT.taxFree],
      conditional: tropicDarkCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      tropicDarkSemantic.info,
      holiday:    tropicDarkSemantic.danger,
      earlyClose: tropicDarkSemantic.warning,
    },
  },

  data: {
    categorical: tropicDarkCategorical,
    sequential: {
      low:  '#0f2a3d',
      mid:  '#206964',
      high: '#73F6FF',
    },
    diverging: {
      negative: '#DF715F',
      neutral:  '#7a8a8e',
      positive: '#80D4B1',
    },
  },
}

// ─── COASTAL LIGHT ─────────────────────────────────────────

const coastalLightSemantic = {
  success: '#2a9e72',
  danger:  '#d47362',
  warning: '#c8a040',
  info:    '#3a7cb8',
  neutral: '#7a8e92',
  accent:  '#206964',
}

const coastalLightCategorical = ['#0c6860', '#5828a0', '#804060', '#6a4820', '#3e5068', '#3e5810']

const coastalLight = {
  surface: {
    base:     '#f5f9f8',
    elevated: '#eaf2f0',
    overlay:  '#dce8e5',
  },

  border: {
    subtle:  '#dce8e5',
    default: '#c4d6d2',
    strong:  '#a8c0ba',
  },

  grid: {
    major: '#c4d6d2',
    minor: '#d3dedb',
  },

  text: {
    primary:   '#1a2e3a',
    secondary: '#3d5a66',
    muted:     '#6b8590',
    bright:    '#f0f8f6',
    link:      '#3d5a66',
  },

  interactive: {
    focus:     '#0F4C81',
    hover:     '#dce8e5',
    active:    '#c4d6d2',
    selection: '#206964',
  },

  opacity: {
    faint:    0.04,
    subtle:   0.12,
    moderate: 0.3,
    strong:   0.65,
  },

  semantic: coastalLightSemantic,

  domain: {
    dividendLifecycle: {
      declare: coastalLightSemantic.danger,
      ex:      coastalLightSemantic.warning,
      record:  coastalLightSemantic.info,
      pay:     coastalLightSemantic.success,
    },
    payments: {
      accent: coastalLightSemantic.accent,
      status: {
        paidRecorded:      coastalLightSemantic.success,
        paidUnrecorded:    coastalLightSemantic.danger,
        expectedAnnounced: coastalLightSemantic.info,
        expectedProjected: coastalLightSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     coastalLightCategorical[TX_SLOT.taxable],
      taxDeferred: coastalLightCategorical[TX_SLOT.taxDeferred],
      taxFree:     coastalLightCategorical[TX_SLOT.taxFree],
      conditional: coastalLightCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      coastalLightSemantic.info,
      holiday:    coastalLightSemantic.danger,
      earlyClose: coastalLightSemantic.warning,
    },
  },

  data: {
    categorical: coastalLightCategorical,
    sequential: {
      low:  '#eaf2f0',
      mid:  '#0F4C81',
      high: '#206964',
    },
    diverging: {
      negative: '#BE5E4E',
      neutral:  '#6b7a7e',
      positive: '#1a7a5a',
    },
  },
}

// ─── LAGOON ────────────────────────────────────────────────

const lagoonSemantic = {
  success: '#2d8a65',
  danger:  '#BE5E4E',
  warning: '#a8872a',
  info:    '#1a7a9a',
  neutral: '#6a8488',
  accent:  '#3aaa80',
}

const lagoonCategorical = ['#58b0b0', '#9078b0', '#b07890', '#a88058', '#7890a0', '#88a050']

const lagoon = {
  surface: {
    base:     '#0c2626',
    elevated: '#102e2e',
    overlay:  '#081a1a',
  },

  border: {
    subtle:  '#183838',
    default: '#204848',
    strong:  '#2a5e5e',
  },

  grid: {
    major: '#204848',
    minor: '#183838',
  },

  text: {
    primary:   '#ffffff',
    secondary: '#c8dede',
    muted:     '#5a8080',
    bright:    '#ffffff',
    link:      '#c8dede',
  },

  interactive: {
    focus:     '#1a7a9a',
    hover:     '#183838',
    active:    '#102e2e',
    selection: '#3aaa80',
  },

  opacity: {
    faint:    0.025,
    subtle:   0.12,
    moderate: 0.45,
    strong:   0.88,
  },

  semantic: lagoonSemantic,

  domain: {
    dividendLifecycle: {
      declare: lagoonSemantic.danger,
      ex:      lagoonSemantic.warning,
      record:  lagoonSemantic.info,
      pay:     lagoonSemantic.success,
    },
    payments: {
      accent: lagoonSemantic.accent,
      status: {
        paidRecorded:      lagoonSemantic.success,
        paidUnrecorded:    lagoonSemantic.danger,
        expectedAnnounced: lagoonSemantic.info,
        expectedProjected: lagoonSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     lagoonCategorical[TX_SLOT.taxable],
      taxDeferred: lagoonCategorical[TX_SLOT.taxDeferred],
      taxFree:     lagoonCategorical[TX_SLOT.taxFree],
      conditional: lagoonCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      lagoonSemantic.info,
      holiday:    lagoonSemantic.danger,
      earlyClose: lagoonSemantic.warning,
    },
  },

  data: {
    categorical: lagoonCategorical,
    sequential: {
      low:  '#102e2e',
      mid:  '#206964',
      high: '#80D4B1',
    },
    diverging: {
      negative: '#BE5E4E',
      neutral:  '#6a8488',
      positive: '#BAFF85',
    },
  },
}

// ─── CLASSIC ──────────────────────────────────────────────
// Matches the pre-theme production timeline: purple record boxes,
// cyan today line, purple projected payments on a dark navy base.

const classicSemantic = {
  success: '#27ae60',
  danger:  '#e94560',
  warning: '#f39c12',
  info:    '#9b59b6',    // Purple (production record/projected color)
  neutral: '#95a5a6',
  accent:  '#00d9ff',    // Cyan (production today line color)
}

const classicCategorical = ['#4db8a0', '#6890c0', '#b87898', '#b88860', '#60a8a0', '#8ca058']

const classic = {
  surface: {
    base:     '#1a1a2e',
    elevated: '#16213e',
    overlay:  '#2a3952',
  },

  border: {
    subtle:  '#2a3952',
    default: '#2a3f5f',
    strong:  '#3b4f73',
  },

  grid: {
    major: '#2a3f5f',
    minor: '#2a3952',
  },

  text: {
    primary:   '#ffffff',
    secondary: '#ecf0f1',
    muted:     '#7f8c8d',
    bright:    '#ffffff',
    link:      '#ecf0f1',
  },

  interactive: {
    focus:     '#9b59b6',
    hover:     '#2a3f5f',
    active:    '#16213e',
    selection: '#00d9ff',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.85,
  },

  semantic: classicSemantic,

  // Domain override: calendar.today → accent (cyan) instead of info
  domain: {
    dividendLifecycle: {
      declare: classicSemantic.danger,
      ex:      classicSemantic.warning,
      record:  classicSemantic.info,
      pay:     classicSemantic.success,
    },
    payments: {
      accent: classicSemantic.accent,
      status: {
        paidRecorded:      classicSemantic.success,
        paidUnrecorded:    classicSemantic.danger,
        expectedAnnounced: classicSemantic.info,
        expectedProjected: classicSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     classicCategorical[TX_SLOT.taxable],
      taxDeferred: classicCategorical[TX_SLOT.taxDeferred],
      taxFree:     classicCategorical[TX_SLOT.taxFree],
      conditional: classicCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      classicSemantic.accent,
      holiday:    classicSemantic.danger,
      earlyClose: classicSemantic.warning,
    },
  },

  data: {
    categorical: classicCategorical,
    sequential: {
      low:  '#0f172a',
      mid:  '#9b59b6',
      high: '#00d9ff',
    },
    diverging: {
      negative: '#e94560',
      neutral:  '#95a5a6',
      positive: '#27ae60',
    },
  },
}

// ─── AMETHYST ─────────────────────────────────────────────
// Deep violet dark theme. Purple-black surfaces with lavender
// accent and teal-green complement.

const amethystSemantic = {
  success: '#2d9668',
  danger:  '#c84858',
  warning: '#c89830',
  info:    '#6858b8',
  neutral: '#8080a0',
  accent:  '#a878e0',
}

const amethystCategorical = ['#48b0a0', '#6090c0', '#b87898', '#b08858', '#88a0a8', '#8ca050']

const amethyst = {
  surface: {
    base:     '#14101e',
    elevated: '#1c1630',
    overlay:  '#261e3a',
  },

  border: {
    subtle:  '#261e3a',
    default: '#302650',
    strong:  '#3e3268',
  },

  grid: {
    major: '#302650',
    minor: '#261e3a',
  },

  text: {
    primary:   '#ffffff',
    secondary: '#d0cce0',
    muted:     '#685e80',
    bright:    '#ffffff',
    link:      '#d0cce0',
  },

  interactive: {
    focus:     '#6858b8',
    hover:     '#261e3a',
    active:    '#1c1630',
    selection: '#a878e0',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.88,
  },

  semantic: amethystSemantic,

  domain: {
    dividendLifecycle: {
      declare: amethystSemantic.danger,
      ex:      amethystSemantic.warning,
      record:  amethystSemantic.info,
      pay:     amethystSemantic.success,
    },
    payments: {
      accent: amethystSemantic.accent,
      status: {
        paidRecorded:      amethystSemantic.success,
        paidUnrecorded:    amethystSemantic.danger,
        expectedAnnounced: amethystSemantic.info,
        expectedProjected: amethystSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     amethystCategorical[TX_SLOT.taxable],
      taxDeferred: amethystCategorical[TX_SLOT.taxDeferred],
      taxFree:     amethystCategorical[TX_SLOT.taxFree],
      conditional: amethystCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      amethystSemantic.info,
      holiday:    amethystSemantic.danger,
      earlyClose: amethystSemantic.warning,
    },
  },

  data: {
    categorical: amethystCategorical,
    sequential: {
      low:  '#1c1630',
      mid:  '#6858b8',
      high: '#a878e0',
    },
    diverging: {
      negative: '#ff7888',
      neutral:  '#8080a0',
      positive: '#50d8a0',
    },
  },
}

// ─── DUSK ─────────────────────────────────────────────────
// Warm sunset dark theme. Rose and copper undertones on dark
// warm gray with coral accent.

const duskSemantic = {
  success: '#3a9860',
  danger:  '#c85050',
  warning: '#c89040',
  info:    '#5080b0',
  neutral: '#908080',
  accent:  '#e07868',
}

const duskCategorical = ['#48b0a0', '#9078b0', '#c08898', '#b08858', '#7890a0', '#889850']

const dusk = {
  surface: {
    base:     '#1a1518',
    elevated: '#241e22',
    overlay:  '#302628',
  },

  border: {
    subtle:  '#302628',
    default: '#3e3234',
    strong:  '#504042',
  },

  grid: {
    major: '#3e3234',
    minor: '#302628',
  },

  text: {
    primary:   '#ffffff',
    secondary: '#dcd4d0',
    muted:     '#706060',
    bright:    '#ffffff',
    link:      '#dcd4d0',
  },

  interactive: {
    focus:     '#5080b0',
    hover:     '#302628',
    active:    '#241e22',
    selection: '#e07868',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.85,
  },

  semantic: duskSemantic,

  domain: {
    dividendLifecycle: {
      declare: duskSemantic.danger,
      ex:      duskSemantic.warning,
      record:  duskSemantic.info,
      pay:     duskSemantic.success,
    },
    payments: {
      accent: duskSemantic.accent,
      status: {
        paidRecorded:      duskSemantic.success,
        paidUnrecorded:    duskSemantic.danger,
        expectedAnnounced: duskSemantic.info,
        expectedProjected: duskSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     duskCategorical[TX_SLOT.taxable],
      taxDeferred: duskCategorical[TX_SLOT.taxDeferred],
      taxFree:     duskCategorical[TX_SLOT.taxFree],
      conditional: duskCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      duskSemantic.info,
      holiday:    duskSemantic.danger,
      earlyClose: duskSemantic.warning,
    },
  },

  data: {
    categorical: duskCategorical,
    sequential: {
      low:  '#241e22',
      mid:  '#c87060',
      high: '#e07868',
    },
    diverging: {
      negative: '#e07868',
      neutral:  '#908080',
      positive: '#50c890',
    },
  },
}

// ─── CLASSIC LIGHT ────────────────────────────────────────
// Light counterpart to Classic. Purple record boxes and blue
// income bars on a subtle blue-white surface.

const classicLightSemantic = {
  success: '#27ae60',    // Production green (portfolio bar)
  danger:  '#dc2626',
  warning: '#d97706',
  info:    '#8e44ad',    // Purple (production record color, adapted for light)
  neutral: '#6b7280',
  accent:  '#2563eb',    // Blue (income bars)
}

const classicLightCategorical = ['#0e7a70', '#4860a8', '#904870', '#7a5028', '#486078', '#4a6818']

const classicLight = {
  surface: {
    base:     '#fafbff',
    elevated: '#f0f2f8',
    overlay:  '#e0e4f0',
  },

  border: {
    subtle:  '#e0e4f0',
    default: '#ccd2e0',
    strong:  '#b0b8cc',
  },

  grid: {
    major: '#ccd2e0',
    minor: '#dadee6',
  },

  text: {
    primary:   '#1f2937',
    secondary: '#4b5563',
    muted:     '#9ca3af',
    bright:    '#ffffff',
    link:      '#4b5563',
  },

  interactive: {
    focus:     '#2563eb',
    hover:     '#e0e4f0',
    active:    '#ccd2e0',
    selection: '#8e44ad',
  },

  opacity: {
    faint:    0.04,
    subtle:   0.12,
    moderate: 0.3,
    strong:   0.65,
  },

  semantic: classicLightSemantic,

  // Domain override: calendar.today → accent instead of info
  domain: {
    dividendLifecycle: {
      declare: classicLightSemantic.danger,
      ex:      classicLightSemantic.warning,
      record:  classicLightSemantic.info,
      pay:     classicLightSemantic.success,
    },
    payments: {
      accent: classicLightSemantic.accent,
      status: {
        paidRecorded:      classicLightSemantic.success,
        paidUnrecorded:    classicLightSemantic.danger,
        expectedAnnounced: classicLightSemantic.info,
        expectedProjected: classicLightSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     classicLightCategorical[TX_SLOT.taxable],
      taxDeferred: classicLightCategorical[TX_SLOT.taxDeferred],
      taxFree:     classicLightCategorical[TX_SLOT.taxFree],
      conditional: classicLightCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      classicLightSemantic.accent,
      holiday:    classicLightSemantic.danger,
      earlyClose: classicLightSemantic.warning,
    },
  },

  data: {
    categorical: classicLightCategorical,
    sequential: {
      low:  '#f0f2f8',
      mid:  '#8e44ad',
      high: '#2563eb',
    },
    diverging: {
      negative: '#dc2626',
      neutral:  '#6b7280',
      positive: '#27ae60',
    },
  },
}

// ─── SAGE ─────────────────────────────────────────────────
// Green-tinted light theme featuring the production portfolio
// bar green. Blue income bars on a natural, warm-cool surface.

const sageSemantic = {
  success: '#27ae60',    // Production green (portfolio bar)
  danger:  '#c0392b',
  warning: '#d4a020',
  info:    '#2980b9',
  neutral: '#7f8c8d',
  accent:  '#27ae60',    // Green (income bars)
}

const sageCategorical = ['#107868', '#6028b0', '#904870', '#785028', '#486078', '#4a6818']

const sage = {
  surface: {
    base:     '#f7faf7',
    elevated: '#eff4ef',
    overlay:  '#dce6dc',
  },

  border: {
    subtle:  '#dce6dc',
    default: '#c4d4c4',
    strong:  '#a8bca8',
  },

  grid: {
    major: '#c4d4c4',
    minor: '#d8e0d8',
  },

  text: {
    primary:   '#1a2e1a',
    secondary: '#3d5a3d',
    muted:     '#6b8a6b',
    bright:    '#142a14',
    link:      '#3d5a3d',
  },

  interactive: {
    focus:     '#2980b9',
    hover:     '#dce6dc',
    active:    '#c4d4c4',
    selection: '#27ae60',
  },

  opacity: {
    faint:    0.04,
    subtle:   0.12,
    moderate: 0.3,
    strong:   0.65,
  },

  semantic: sageSemantic,

  domain: {
    dividendLifecycle: {
      declare: sageSemantic.danger,
      ex:      sageSemantic.warning,
      record:  sageSemantic.info,
      pay:     sageSemantic.success,
    },
    payments: {
      accent: sageSemantic.accent,
      status: {
        paidRecorded:      sageSemantic.success,
        paidUnrecorded:    sageSemantic.danger,
        expectedAnnounced: sageSemantic.info,
        expectedProjected: sageSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     sageCategorical[TX_SLOT.taxable],
      taxDeferred: sageCategorical[TX_SLOT.taxDeferred],
      taxFree:     sageCategorical[TX_SLOT.taxFree],
      conditional: sageCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      sageSemantic.info,
      holiday:    sageSemantic.danger,
      earlyClose: sageSemantic.warning,
    },
  },

  data: {
    categorical: sageCategorical,
    sequential: {
      low:  '#eff4ef',
      mid:  '#27ae60',
      high: '#3478c0',
    },
    diverging: {
      negative: '#c0392b',
      neutral:  '#7f8c8d',
      positive: '#27ae60',
    },
  },
}

// ─── PORCELAIN ────────────────────────────────────────────
// Cool, refined blue-white surface with indigo info and plum
// accent. Clean and crisp.

const porcelainSemantic = {
  success: '#27ae60',    // Production green (portfolio bar)
  danger:  '#d04040',
  warning: '#d09020',
  info:    '#5c6bc0',    // Indigo (production "line" color callback)
  neutral: '#78909c',
  accent:  '#8854a0',    // Plum
}

const porcelainCategorical = ['#0e7068', '#5830a0', '#884868', '#725028', '#405870', '#486018']

const porcelain = {
  surface: {
    base:     '#f8f9fc',
    elevated: '#eff1f6',
    overlay:  '#dde0ea',
  },

  border: {
    subtle:  '#dde0ea',
    default: '#c8cdd8',
    strong:  '#aeb4c4',
  },

  grid: {
    major: '#c8cdd8',
    minor: '#d8dce4',
  },

  text: {
    primary:   '#1e2536',
    secondary: '#4a5568',
    muted:     '#8a96a8',
    bright:    '#1a2030',
    link:      '#4a5568',
  },

  interactive: {
    focus:     '#5c6bc0',
    hover:     '#dde0ea',
    active:    '#c8cdd8',
    selection: '#8854a0',
  },

  opacity: {
    faint:    0.04,
    subtle:   0.12,
    moderate: 0.3,
    strong:   0.65,
  },

  semantic: porcelainSemantic,

  domain: {
    dividendLifecycle: {
      declare: porcelainSemantic.danger,
      ex:      porcelainSemantic.warning,
      record:  porcelainSemantic.info,
      pay:     porcelainSemantic.success,
    },
    payments: {
      accent: porcelainSemantic.accent,
      status: {
        paidRecorded:      porcelainSemantic.success,
        paidUnrecorded:    porcelainSemantic.danger,
        expectedAnnounced: porcelainSemantic.info,
        expectedProjected: porcelainSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     porcelainCategorical[TX_SLOT.taxable],
      taxDeferred: porcelainCategorical[TX_SLOT.taxDeferred],
      taxFree:     porcelainCategorical[TX_SLOT.taxFree],
      conditional: porcelainCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      porcelainSemantic.info,
      holiday:    porcelainSemantic.danger,
      earlyClose: porcelainSemantic.warning,
    },
  },

  data: {
    categorical: porcelainCategorical,
    sequential: {
      low:  '#eff1f6',
      mid:  '#5c6bc0',
      high: '#8854a0',
    },
    diverging: {
      negative: '#d04040',
      neutral:  '#78909c',
      positive: '#27ae60',
    },
  },
}

// ─── MONOCHROME DARK ────────────────────────────────────────
// Pure grayscale dark theme. Zero hue — every value is R=G=B.
// Invert all channels (255−x) to get Monochrome Light.

const monochromeDarkSemantic = {
  success: '#909090',
  danger:  '#505050',
  warning: '#787878',
  info:    '#686868',
  neutral: '#606060',
  accent:  '#c0c0c0',
}

const monochromeDarkCategorical = ['#c8c8c8', '#3c3c3c', '#a8a8a8', '#2c2c2c', '#b0b0b0', '#4c4c4c']

const monochromeDark = {
  surface: {
    base:     '#141414',
    elevated: '#1e1e1e',
    overlay:  '#0a0a0a',
  },

  border: {
    subtle:  '#282828',
    default: '#363636',
    strong:  '#484848',
  },

  grid: {
    major: '#363636',
    minor: '#282828',
  },

  text: {
    primary:   '#d0d0d0',
    secondary: '#909090',
    muted:     '#585858',
    bright:    '#ffffff',
    link:      '#909090',
  },

  interactive: {
    focus:     '#a0a0a0',
    hover:     '#282828',
    active:    '#1e1e1e',
    selection: '#c0c0c0',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.9,
  },

  semantic: monochromeDarkSemantic,

  domain: {
    dividendLifecycle: {
      declare: monochromeDarkSemantic.danger,
      ex:      monochromeDarkSemantic.warning,
      record:  monochromeDarkSemantic.info,
      pay:     monochromeDarkSemantic.success,
    },
    payments: {
      accent: monochromeDarkSemantic.accent,
      status: {
        paidRecorded:      monochromeDarkSemantic.success,
        paidUnrecorded:    monochromeDarkSemantic.danger,
        expectedAnnounced: monochromeDarkSemantic.info,
        expectedProjected: monochromeDarkSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     monochromeDarkCategorical[TX_SLOT.taxable],
      taxDeferred: monochromeDarkCategorical[TX_SLOT.taxDeferred],
      taxFree:     monochromeDarkCategorical[TX_SLOT.taxFree],
      conditional: monochromeDarkCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      monochromeDarkSemantic.info,
      holiday:    monochromeDarkSemantic.danger,
      earlyClose: monochromeDarkSemantic.warning,
    },
  },

  data: {
    categorical: monochromeDarkCategorical,
    sequential: {
      low:  '#1e1e1e',
      mid:  '#686868',
      high: '#c0c0c0',
    },
    diverging: {
      negative: '#505050',
      neutral:  '#606060',
      positive: '#909090',
    },
  },
}

// ─── MONOCHROME LIGHT ───────────────────────────────────────
// Exact negative of Monochrome Dark. Every channel = 255 − dark channel.

const monochromeLightSemantic = {
  success: '#6f6f6f',
  danger:  '#afafaf',
  warning: '#878787',
  info:    '#979797',
  neutral: '#9f9f9f',
  accent:  '#3f3f3f',
}

const monochromeLightCategorical = ['#373737', '#c3c3c3', '#575757', '#d3d3d3', '#4f4f4f', '#b3b3b3']

const monochromeLight = {
  surface: {
    base:     '#ebebeb',
    elevated: '#e1e1e1',
    overlay:  '#f5f5f5',
  },

  border: {
    subtle:  '#d7d7d7',
    default: '#c9c9c9',
    strong:  '#b7b7b7',
  },

  grid: {
    major: '#c9c9c9',
    minor: '#d7d7d7',
  },

  text: {
    primary:   '#2f2f2f',
    secondary: '#6f6f6f',
    muted:     '#a7a7a7',
    bright:    '#000000',
    link:      '#6f6f6f',
  },

  interactive: {
    focus:     '#5f5f5f',
    hover:     '#d7d7d7',
    active:    '#e1e1e1',
    selection: '#3f3f3f',
  },

  opacity: {
    faint:    0.03,
    subtle:   0.15,
    moderate: 0.5,
    strong:   0.9,
  },

  semantic: monochromeLightSemantic,

  domain: {
    dividendLifecycle: {
      declare: monochromeLightSemantic.danger,
      ex:      monochromeLightSemantic.warning,
      record:  monochromeLightSemantic.info,
      pay:     monochromeLightSemantic.success,
    },
    payments: {
      accent: monochromeLightSemantic.accent,
      status: {
        paidRecorded:      monochromeLightSemantic.success,
        paidUnrecorded:    monochromeLightSemantic.danger,
        expectedAnnounced: monochromeLightSemantic.info,
        expectedProjected: monochromeLightSemantic.warning,
      },
    },
    taxTreatment: {
      taxable:     monochromeLightCategorical[TX_SLOT.taxable],
      taxDeferred: monochromeLightCategorical[TX_SLOT.taxDeferred],
      taxFree:     monochromeLightCategorical[TX_SLOT.taxFree],
      conditional: monochromeLightCategorical[TX_SLOT.conditional],
    },
    calendar: {
      today:      monochromeLightSemantic.info,
      holiday:    monochromeLightSemantic.danger,
      earlyClose: monochromeLightSemantic.warning,
    },
  },

  data: {
    categorical: monochromeLightCategorical,
    sequential: {
      low:  '#e1e1e1',
      mid:  '#979797',
      high: '#3f3f3f',
    },
    diverging: {
      negative: '#afafaf',
      neutral:  '#9f9f9f',
      positive: '#6f6f6f',
    },
  },
}

// ─── EXPORTS ─────────────────────────────────────────────────

export const THEMES = {
  dark, light, solarizedDark, solarizedLight, midnight, paper, highContrast,
  tropicDark, coastalLight, lagoon, classic, amethyst, dusk,
  classicLight, sage, porcelain, monochromeDark, monochromeLight,
}

export const THEME_DISPLAY_NAMES = {
  dark:            'Dark',
  light:           'Light',
  solarizedDark:   'Solarized Dark',
  solarizedLight:  'Solarized Light',
  midnight:        'Midnight Blue',
  paper:           'Paper Light',
  highContrast:    'High Contrast',
  tropicDark:      'Tropic Dark',
  coastalLight:    'Coastal Light',
  lagoon:          'Lagoon',
  classic:         'Classic',
  amethyst:        'Amethyst',
  dusk:            'Dusk',
  classicLight:    'Classic Light',
  sage:            'Sage',
  porcelain:       'Porcelain',
  monochromeDark:  'Monochrome Dark',
  monochromeLight: 'Monochrome Light',
}

export function getTheme(name) {
  if (!THEMES[name]) {
    console.error(`Unknown theme: '${name}'. Available: ${Object.keys(THEMES).join(', ')}`)
    return THEMES.dark
  }
  return THEMES[name]
}

/**
 * Derive an rgba() color from a hex base + alpha.
 * Used to build gradients from a single accent color.
 * Alpha is clamped to [0, 1] to prevent invisible bugs.
 */
export function withAlpha(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const a = Math.min(Math.max(alpha, 0), 1)
  return `rgba(${r}, ${g}, ${b}, ${a})`
}

/**
 * Derive a brightness-adjusted hex color from a base hex.
 * factor > 1 brightens, factor < 1 darkens. Clamped to valid RGB.
 * Used to differentiate multiple accounts within the same tax treatment.
 */
function adjustBrightness(hex, factor) {
  const r = Math.min(255, Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * factor)))
  const g = Math.min(255, Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * factor)))
  const b = Math.min(255, Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * factor)))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

/**
 * Shade a base color by rank index within a group.
 * Index 0 returns the base color unchanged; higher indices get progressively darker.
 * Used to give each account a unique shade within its tax treatment group.
 */
export function shadeByIndex(base, index) {
  if (index === 0) return base
  return adjustBrightness(base, Math.max(1.0 - (index * 0.20), 0.40))
}

// TODO: Unify naming — either change theme keys to snake_case or API values to camelCase
// so this mapping becomes unnecessary. If account colors were looked up by ID instead of
// derived from tax treatment, this mapping could be eliminated entirely.
const TAX_TREATMENT_KEY = {
  taxable: 'taxable',
  tax_deferred: 'taxDeferred',
  tax_free: 'taxFree',
  conditional: 'conditional'
}

export function normalizeTaxTreatment(value) {
  return TAX_TREATMENT_KEY[value]
}
